/**
 * Norwegian organisation number (organisasjonsnummer): 9 digits, the last a mod-11 check digit.
 * Formatting per Non-Functional.md: `123 456 789`.
 */

declare const orgnrBrand: unique symbol;
export type Orgnr = string & { readonly [orgnrBrand]: true };

const WEIGHTS = [3, 2, 7, 6, 5, 4, 3, 2] as const;

/** Mod-11 check digit for the first eight digits, or null when no valid check digit exists. */
export function orgnrCheckDigit(firstEight: string): number | null {
  if (!/^\d{8}$/.test(firstEight)) return null;
  const sum = WEIGHTS.reduce((acc, weight, i) => acc + Number(firstEight.charAt(i)) * weight, 0);
  const remainder = sum % 11;
  if (remainder === 0) return 0;
  const check = 11 - remainder;
  return check === 10 ? null : check;
}

/** Accepts `123456789` or `123 456 789`; returns the bare Orgnr or null if malformed or failing mod-11. */
export function parseOrgnr(input: string): Orgnr | null {
  const digits = input.replace(/\s+/g, '');
  if (!/^\d{9}$/.test(digits)) return null;
  const check = orgnrCheckDigit(digits.slice(0, 8));
  if (check === null || check !== Number(digits[8])) return null;
  return digits as Orgnr;
}

export function isOrgnr(input: string): input is Orgnr {
  return parseOrgnr(input) === input;
}

export function formatOrgnr(orgnr: Orgnr): string {
  return `${orgnr.slice(0, 3)} ${orgnr.slice(3, 6)} ${orgnr.slice(6)}`;
}
